import { useCallback, useState, type ReactNode, type RefObject } from 'react';
import type { CSSProperties } from 'react';
import { FileReviewChat, type FileReviewChatProps } from './FileReviewChat';
import type { FileReviewPanelPlacement } from './types';
import './FileReviewWorkspace.css';

const AUTO_HIDE_HOVER_STRIP_PX = 10;

export interface FileReviewPanelProps extends Omit<FileReviewChatProps, 'collapsed' | 'panelStyle'> {
  open: boolean;
  placement?: FileReviewPanelPlacement;
  panelStyle?: CSSProperties;
  onResizeStart?: (event: React.MouseEvent) => void;
  onResizeReset?: () => void;
}

export function FileReviewPanel({
  open,
  placement = 'right',
  panelStyle,
  onResizeStart,
  onResizeReset,
  ...chatProps
}: FileReviewPanelProps) {
  return (
    <div
      className={`file-review-panel-zone file-review-panel-zone--${placement}${open ? ' file-review-panel-zone--open' : ''}`}
    >
      <FileReviewChat collapsed={!open} panelStyle={open ? panelStyle : undefined} {...chatProps} />
      {open && onResizeStart ? (
        <div
          className={`file-review-panel__resizer file-review-panel__resizer--${placement}`}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize discussion panel"
          onMouseDown={onResizeStart}
          onDoubleClick={onResizeReset}
          title="Drag to resize, double-click to reset"
        />
      ) : null}
    </div>
  );
}

export interface FileReviewWorkspaceProps {
  workspaceRef?: RefObject<HTMLDivElement | null>;
  placement?: FileReviewPanelPlacement;
  panelOpen: boolean;
  autoHide?: boolean;
  panelProps: Omit<FileReviewPanelProps, 'open' | 'placement'>;
  viewerClassName?: string;
  children: ReactNode;
}

export function FileReviewWorkspace({
  workspaceRef,
  placement = 'right',
  panelOpen,
  autoHide = false,
  panelProps,
  viewerClassName = '',
  children
}: FileReviewWorkspaceProps) {
  const [hoverOpen, setHoverOpen] = useState(false);
  const useAutoHide = autoHide && placement !== 'off';
  const effectiveOpen =
    placement !== 'off' && (useAutoHide ? panelOpen || hoverOpen : panelOpen);

  const openHover = useCallback(() => {
    if (useAutoHide && !panelOpen) setHoverOpen(true);
  }, [panelOpen, useAutoHide]);

  const closeHover = useCallback(() => {
    if (useAutoHide && !panelOpen) setHoverOpen(false);
  }, [panelOpen, useAutoHide]);

  const panel = (
    <div
      className="file-review-workspace__panel-zone"
      onMouseEnter={useAutoHide ? openHover : undefined}
      onMouseLeave={useAutoHide ? closeHover : undefined}
    >
      <FileReviewPanel
        {...panelProps}
        open={effectiveOpen}
        placement={placement}
        panelStyle={effectiveOpen ? panelProps.panelStyle : undefined}
      />
    </div>
  );

  const hoverStrip =
    useAutoHide && !effectiveOpen ? (
      <div
        className={`file-review-workspace__hover-strip file-review-workspace__hover-strip--${placement}`}
        style={
          placement === 'left' || placement === 'right'
            ? { width: AUTO_HIDE_HOVER_STRIP_PX }
            : undefined
        }
        aria-hidden
        onMouseEnter={openHover}
      />
    ) : null;

  return (
    <div
      ref={workspaceRef as RefObject<HTMLDivElement> | undefined}
      className={`file-review-workspace file-review-workspace--${placement}${effectiveOpen ? ' file-review-workspace--panel-open' : ''}${useAutoHide ? ' file-review-workspace--auto-hide' : ''}`}
    >
      {placement === 'left' ? (
        <>
          {hoverStrip}
          {panel}
        </>
      ) : null}
      <div className={`file-review-workspace__viewer ${viewerClassName}`.trim()}>{children}</div>
      {placement === 'right' ? (
        <>
          {hoverStrip}
          {panel}
        </>
      ) : null}
    </div>
  );
}
